<template>
  <div class="whatsapp-message-templates-page">
    <div class="action-buttons">
      <Button
        @click="openModal"
        :disabled="isLoading"
        severity="primary"
        :label="t('Novo Template')"
        icon="pi pi-plus"
        data-testid="whatsapp-templates-new-button"
      />
    </div>

    <!-- DataTable with clickable_id and refresh -->
    <data-table
      ref="dataTableRef"
      :config="datatableConfig"
      :data="tableData"
      :filterConfig="filterConfig"
      :refreshButton="{ show: true }"
      @edit-click="editItem"
      @button-click="handleButtonClick"
      @refresh="loadData"
    />

    <!-- WhatsappMessageTemplatesModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useWhatsappMessageTemplates } from '../../composables/useWhatsappMessageTemplates';

interface TemplateRow {
  template_id: number | string;
  template_name?: string;
  template_type?: string;
  template_content?: string;
  is_active?: number | boolean | string;
  franquias_nomes?: string;
  [key: string]: unknown;
}

interface ButtonClickPayload {
  value: number | string;
  data: TemplateRow;
  action: string;
}

defineProps<{
  translations?: Record<string, string>;
}>();

const { t } = useI18n();
const { fetchTemplates } = useWhatsappMessageTemplates();

// Reactive data
const tableData = ref<TemplateRow[]>([]);
const isLoading = ref<boolean>(false);
const dataTableRef = ref<unknown>(null);

// Column config with clickable_id as first column
const tableColumns = computed(() => [
  {
    data: 'template_id',
    title: t('ID'),
    renderType: 'clickable_id',
    footerType: 'count'
  },
  {
    data: 'template_name',
    title: t('Nome')
  },
  {
    data: 'template_type',
    title: t('Tipo'),
    renderType: 'badge',
    badgeConfig: (row: TemplateRow) => {
      const typeMap: Record<string, { label: string; severity: string }> = {
        text: { label: 'Texto', severity: 'info' },
        image: { label: 'Imagem', severity: 'success' },
        video: { label: 'Vídeo', severity: 'warn' },
        audio: { label: 'Áudio', severity: 'secondary' }
      };
      const config = typeMap[row.template_type as string] || typeMap.text;
      return { label: config.label, severity: config.severity };
    }
  },
  {
    data: 'template_content',
    title: t('Conteudo')
  },
  {
    data: 'is_active',
    title: t('Status'),
    renderType: 'yes_no'
  },
  {
    data: 'franquias_nomes',
    title: t('Franquias')
  },
  {
    data: 'template_id',
    title: t('Excluir'),
    sortable: false,
    renderType: 'clickable_button',
    buttonText: t('Excluir'),
    buttonIcon: 'pi pi-trash',
    buttonClass: 'p-button-danger p-button-sm',
    buttonAction: 'delete'
  }
]);

const datatableConfig = computed(() => ({
  tableId: 'whatsappMessageTemplatesDataTable',
  columns: tableColumns.value,
  pagination: true,
  pageLength: 25,
  filterDisplay: 'row',
  heightMode: 'fill-viewport',
}));

const filterConfig = computed(() => ({
  columns: {
    'template_id': 'text',
    'template_name': 'text',
    'template_type': 'select',
    'template_content': 'text',
    'is_active': 'select',
    'franquias_nomes': 'text'
  }
}));

// Methods
const loadData = async (): Promise<void> => {
  log('WhatsappMessageTemplatesPage', 'loadData', 'Loading templates');
  try {
    isLoading.value = true;
    const templates = (await fetchTemplates()) as unknown as TemplateRow[];
    tableData.value = templates;
    log('WhatsappMessageTemplatesPage', 'loadData', 'Loaded templates', templates.length);
  } catch (error) {
    const err = error as Error;
    log('WhatsappMessageTemplatesPage', 'loadData', 'Error', err?.message || err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar templates') + ': ' + err.message);
  } finally {
    isLoading.value = false;
  }
};

const openModal = (): void => {
  log('WhatsappMessageTemplatesPage', 'openModal', 'Opening new template modal via eventBus');
  eventBus.emit('modal.whatsappMessageTemplates.open', { mode: 'create' });
};

const editItem = (templateId: number | string): void => {
  log('WhatsappMessageTemplatesPage', 'editItem', 'Editing template ID via eventBus', templateId);
  eventBus.emit('modal.whatsappMessageTemplates.open', { id: templateId, mode: 'edit' });
};

const handleButtonClick = ({ value, data, action }: ButtonClickPayload): void => {
  log('WhatsappMessageTemplatesPage', 'handleButtonClick', 'Button clicked', { value, action });
  if (action === 'delete') {
    eventBus.emit('modal.whatsappMessageTemplates.open', { id: value, data, mode: 'delete' });
  }
};

// Event handlers
const handleRefresh = (): void => {
  log('WhatsappMessageTemplatesPage', 'event:refresh', 'Received');
  loadData();
};

// Lifecycle
onMounted(async () => {
  log('WhatsappMessageTemplatesPage', 'mounted', 'Component mounted');
  await loadData();

  eventBus.on('whatsappMessageTemplates.created', handleRefresh);
  eventBus.on('whatsappMessageTemplates.updated', handleRefresh);
  eventBus.on('whatsappMessageTemplates.deleted', handleRefresh);
  eventBus.on('filter.updated', handleRefresh);
});

onBeforeUnmount(() => {
  log('WhatsappMessageTemplatesPage', 'beforeUnmount', 'Cleaning up');
  eventBus.off('whatsappMessageTemplates.created', handleRefresh);
  eventBus.off('whatsappMessageTemplates.updated', handleRefresh);
  eventBus.off('whatsappMessageTemplates.deleted', handleRefresh);
  eventBus.off('filter.updated', handleRefresh);
});

defineExpose({
  loadData,
  editItem,
  openModal
});
</script>

<style scoped>
.whatsapp-message-templates-page {
  width: 100%;
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
  margin-bottom: 20px;
}
</style>
